import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, FileSpreadsheet, FileText, Search, Loader2, ListChecks, Package, Tags } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';

function AdminPriceList({ user, onLogout }) {
  const { i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [availableColumns, setAvailableColumns] = useState([]);

  const [title, setTitle] = useState('Прайс-лист Ainanan');
  const [scope, setScope] = useState('all'); // all | categories | products
  const [selectedCats, setSelectedCats] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [columns, setColumns] = useState(['name', 'price_b2b', 'unit']);
  const [exportLang, setExportLang] = useState('ru');
  const [onlyActive, setOnlyActive] = useState(true);
  const [productSearch, setProductSearch] = useState('');
  const [downloading, setDownloading] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/admin/price-list/options`, { headers: getAuthHeaders() });
        setCategories(res.data.categories || []);
        setProducts(res.data.products || []);
        setAvailableColumns(res.data.available_columns || []);
      } catch (e) {
        toast.error('Не удалось загрузить данные');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const pName = (p) => (exportLang === 'kz' ? p.name_kz : exportLang === 'en' ? p.name_en : p.name_ru) || p.name_ru || '';
  const cName = (c) => (exportLang === 'kz' ? c.name_kz : exportLang === 'en' ? c.name_en : c.name_ru) || c.name_ru || '';

  const filteredProducts = useMemo(() => {
    let list = onlyActive ? products.filter((p) => p.is_active) : products;
    if (productSearch.trim()) {
      const q = productSearch.toLowerCase();
      list = list.filter((p) => (pName(p) || '').toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q) || (p.article || '').toLowerCase().includes(q));
    }
    return list;
  }, [products, productSearch, onlyActive, exportLang]);

  const selectedCount = useMemo(() => {
    const active = onlyActive ? products.filter((p) => p.is_active) : products;
    if (scope === 'all') return active.length;
    if (scope === 'products') return selectedProducts.length;
    const set = new Set(selectedCats);
    return active.filter((p) => set.has(p.category_id)).length;
  }, [scope, selectedCats, selectedProducts, products, onlyActive]);

  const toggle = (arr, setArr, val) => {
    setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  };

  const handleExport = async (format) => {
    if (columns.length === 0) { toast.error('Выберите хотя бы одну колонку'); return; }
    if (scope === 'categories' && selectedCats.length === 0) { toast.error('Выберите категории'); return; }
    if (scope === 'products' && selectedProducts.length === 0) { toast.error('Выберите товары'); return; }
    setDownloading(format);
    try {
      const res = await axios.post(`${API}/admin/price-list/export`, {
        format, columns, scope,
        category_ids: selectedCats, product_ids: selectedProducts,
        lang: exportLang, title, include_logo: true, only_active: onlyActive,
      }, { headers: getAuthHeaders(), responseType: 'blob' });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `price_list_${new Date().toISOString().slice(0, 10)}.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(format === 'pdf' ? 'PDF готов' : 'Excel готов');
    } catch (e) {
      let msg = 'Ошибка экспорта';
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) {}
      toast.error(msg);
    } finally {
      setDownloading('');
    }
  };

  const ScopeBtn = ({ value, icon: Icon, label }) => (
    <button
      onClick={() => setScope(value)}
      data-testid={`scope-${value}-btn`}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${scope === value ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-stone-700 border-stone-200 hover:border-emerald-400'}`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/${currentLang}/admin`)} data-testid="back-to-admin-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Админ
            </Button>
            <h1 className="text-lg font-bold text-stone-900 flex items-center gap-2">
              <ListChecks className="w-5 h-5 text-emerald-600" /> Конструктор прайс-листа
            </h1>
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Scope */}
          <Card>
            <CardHeader><CardTitle className="text-base">1. Что включить в прайс</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <ScopeBtn value="all" icon={Package} label="Все товары" />
                <ScopeBtn value="categories" icon={Tags} label="По категориям" />
                <ScopeBtn value="products" icon={ListChecks} label="Выбрать товары" />
              </div>

              {scope === 'categories' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto border rounded-lg p-3">
                  {categories.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer py-1" data-testid={`cat-row-${c.id}`}>
                      <Checkbox checked={selectedCats.includes(c.id)} onCheckedChange={() => toggle(selectedCats, setSelectedCats, c.id)} data-testid={`cat-check-${c.id}`} />
                      <span className="flex-1">{cName(c)}</span>
                      <Badge variant="secondary" className="text-xs">{c.product_count}</Badge>
                    </label>
                  ))}
                </div>
              )}

              {scope === 'products' && (
                <div>
                  <div className="relative mb-2">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input placeholder="Поиск по названию, SKU, артикулу" value={productSearch} onChange={(e) => setProductSearch(e.target.value)} className="pl-9" data-testid="product-search-input" />
                  </div>
                  <div className="flex items-center justify-between mb-2 text-xs text-stone-500">
                    <span>Найдено: {filteredProducts.length}</span>
                    <div className="flex gap-3">
                      <button className="text-emerald-700 hover:underline" onClick={() => setSelectedProducts([...new Set([...selectedProducts, ...filteredProducts.map((p) => p.id)])])} data-testid="select-all-found-btn">Выбрать все найденные</button>
                      <button className="text-stone-500 hover:underline" onClick={() => setSelectedProducts([])} data-testid="clear-selection-btn">Сбросить</button>
                    </div>
                  </div>
                  <div className="max-h-80 overflow-y-auto border rounded-lg p-3 space-y-1">
                    {filteredProducts.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer py-1" data-testid={`product-row-${p.id}`}>
                        <Checkbox checked={selectedProducts.includes(p.id)} onCheckedChange={() => toggle(selectedProducts, setSelectedProducts, p.id)} data-testid={`product-check-${p.id}`} />
                        <span className="flex-1">{pName(p)}</span>
                        {p.sku && <span className="text-xs text-stone-400">{p.sku}</span>}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Columns */}
          <Card>
            <CardHeader><CardTitle className="text-base">2. Какие колонки показать</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {availableColumns.filter((c) => c.key !== 'num').map((col) => (
                  <label key={col.key} className="flex items-center gap-2 text-sm cursor-pointer py-1" data-testid={`col-row-${col.key}`}>
                    <Checkbox checked={columns.includes(col.key)} onCheckedChange={() => toggle(columns, setColumns, col.key)} data-testid={`col-check-${col.key}`} />
                    <span>{col.label}</span>
                    {col.pdf_only && <Badge variant="outline" className="text-[10px] py-0">PDF</Badge>}
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Settings + download */}
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-base">3. Настройки</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm">Заголовок прайса</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" data-testid="title-input" />
              </div>
              <div>
                <Label className="text-sm">Язык названий</Label>
                <div className="flex gap-2 mt-1">
                  {['ru', 'kz', 'en'].map((l) => (
                    <button key={l} onClick={() => setExportLang(l)} data-testid={`lang-${l}-btn`}
                      className={`px-3 py-1.5 rounded-md text-sm border ${exportLang === l ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white border-stone-200'}`}>{l.toUpperCase()}</button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={onlyActive} onCheckedChange={() => setOnlyActive(!onlyActive)} data-testid="only-active-check" />
                Только активные товары
              </label>
            </CardContent>
          </Card>

          <Card className="border-emerald-200">
            <CardContent className="pt-6 space-y-3">
              <div className="text-sm text-stone-600 flex items-center justify-between">
                <span>Будет в прайсе:</span>
                <span className="font-bold text-emerald-700 text-lg" data-testid="selected-count">{selectedCount} поз.</span>
              </div>
              <Button onClick={() => handleExport('xlsx')} disabled={!!downloading} className="w-full bg-emerald-700 hover:bg-emerald-800" data-testid="download-xlsx-btn">
                {downloading === 'xlsx' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
                Скачать Excel
              </Button>
              <Button onClick={() => handleExport('pdf')} disabled={!!downloading} variant="outline" className="w-full border-emerald-300 text-emerald-800 hover:bg-emerald-50" data-testid="download-pdf-btn">
                {downloading === 'pdf' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Скачать PDF
              </Button>
              <p className="text-xs text-stone-400 text-center">Колонка «Фото» добавляется только в PDF</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default AdminPriceList;
